<template>
    <div class="animated fadeIn">
        <b-row>
            <b-col cols="12" md="12">
                <ComponentHeader
                    title="Campaigns"
                    :hasSearch="true"
                    :hasCreate="true"
                    createButtonText="Create Campaign"
                    @create-press="showAddCampaignModal"
                    :hasReload="false"
                    :hasback="false"
                    @search-query="(val) => (searchQuery = val)"
                >
                    
                </ComponentHeader>
            </b-col>
        </b-row>

        <!-- Overview Cards -->
        <b-row class="overview-row">
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap overview-card">
                    <div class="overview-label">Total Campaigns</div>
                    <div class="overview-value">{{ stats.totalCampaigns }}</div>
                </b-card>
            </b-col>
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap overview-card">
                    <div class="overview-label">Active Campaigns</div>
                    <div class="overview-value">
                        {{ stats.activeCampaigns }}
                    </div>
                </b-card>
            </b-col>
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap overview-card">
                    <div class="overview-label">Total Sent</div>
                    <div class="overview-value">
                        {{ formatNumber(stats.totalSent) }}
                    </div>
                </b-card>
            </b-col>
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap overview-card">
                    <div class="overview-label">Avg Open Rate</div>
                    <div class="overview-value">{{ stats.avgOpenRate }}</div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Campaigns Table -->
        <b-row>
            <b-col cols="12" md="12">
                <b-card class="card-wrap campaigns-table-card" border-variant="info">
                    <b-table
                        id="campaigns-table"
                        class="tableRadius professional-stacked-table"
                        responsive
                        stacked="md"
                        :items="filteredCampaigns"
                        :fields="tableFields"
                        :current-page="currentPage"
                        :per-page="perPage"
                        thead-class="header"
                        show-empty
                        hover
                        bordered
                    >
                        <template slot="cell(name)" slot-scope="data">
                            <div class="font-weight-bold">
                                {{ data.item.name }}
                            </div>
                        </template>

                        <template slot="cell(type)" slot-scope="data">
                            <b-badge :variant="'light'" class="type-badge">
                                {{ data.item.type }}
                            </b-badge>
                        </template>

                        <template slot="cell(targetAudience)" slot-scope="data">
                            {{ data.item.targetAudience }}
                        </template>

                        <template slot="cell(startEnd)" slot-scope="data">
                            <div class="small text-muted">
                                {{ formatDate(data.item.startDate) }}
                                <br />
                                {{ formatDate(data.item.endDate) }}
                            </div>
                        </template>

                        <template slot="cell(status)" slot-scope="data">
                            <b-badge
                                :class="['status-badge', data.item.status]"
                            >
                                {{ data.item.status }}
                            </b-badge>
                        </template>

                        <template slot="cell(sent)" slot-scope="data">
                            {{ formatNumber(data.item.sent) }}
                        </template>

                        <template slot="cell(openRate)" slot-scope="data">
                            {{ data.item.openRate }}
                        </template>

                        <template slot="cell(clickRate)" slot-scope="data">
                            {{ data.item.clickRate }}
                        </template>

                        <template slot="cell(actions)" slot-scope="data">
                            <ActionButtonGroup
                                :showView="true"
                                @view="viewCampaigns(data.item)"
                            />
                        </template>
                        <template slot="empty">
                            <div class="text-center py-4">
                                <p>No campaigns found.</p>
                            </div>
                        </template>
                    </b-table>
                    <div class="pagination-wrapper">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            aria-controls="campaigns-table"
                            prev-text="Prev"
                            next-text="Next"
                            hide-goto-end-buttons
                            class="mt-3 custom-pagination"
                        ></b-pagination>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- View Campaign Modal -->
        <b-modal
            id="viewCampaignModal"
            ref="viewCampaignModal"
            size="lg"
            centered
            hide-footer
            :title="selectedCampaign ? selectedCampaign.name : 'Campaign Details'"
            modal-class="center-title-modal campaigns-modal"
        >
            <template slot="default">
                <p class="modal-subtitle">
                    Campaign details and performance summary.
                </p>
                <div v-if="selectedCampaign" class="campaign-modal-badges">
                    <b-badge
                        :class="[
                            'campaign-status-badge',
                            selectedCampaign.status,
                        ]"
                    >
                        {{ selectedCampaign.status }}
                    </b-badge>
                    <b-badge class="campaign-type-badge">
                        {{ selectedCampaign.type }}
                    </b-badge>
                </div>

                <!-- Campaign Details -->
                <div class="view-campaign-body">
                    <div class="campaign-details-section">
                        <div class="campaign-detail-item">
                            <div class="campaign-detail-label">
                                Target Audience
                            </div>
                            <div class="campaign-detail-value">
                                {{
                                    selectedCampaign
                                        ? selectedCampaign.targetAudience
                                        : ""
                                }}
                            </div>
                        </div>
                        <div class="campaign-detail-item">
                            <div class="campaign-detail-label">
                                Campaign Period
                            </div>
                            <div class="campaign-detail-value">
                                {{
                                    selectedCampaign
                                        ? `${formatDate(selectedCampaign.startDate)} - ${formatDate(selectedCampaign.endDate)}`
                                        : ""
                                }}
                            </div>
                        </div>
                    </div>

                    <!-- Performance Metrics -->
                    <div class="performance-metrics-section">
                        <h5 class="section-title">Performance Metrics</h5>
                        <div class="metrics-grid">
                            <div class="metric-card">
                                <div class="metric-icon sent-icon">
                                    <i class="fa fa-users"></i>
                                </div>
                                <div class="metric-content">
                                    <div class="metric-label">Sent</div>
                                    <div class="metric-value">
                                        {{
                                            selectedCampaign
                                                ? formatNumber(
                                                      selectedCampaign.sent,
                                                  )
                                                : "0"
                                        }}
                                    </div>
                                </div>
                            </div>
                            <div class="metric-card">
                                <div class="metric-icon open-icon">
                                    <i class="fa fa-eye"></i>
                                </div>
                                <div class="metric-content">
                                    <div class="metric-label">Open Rate</div>
                                    <div class="metric-value">
                                        {{
                                            selectedCampaign
                                                ? selectedCampaign.openRate
                                                : "0%"
                                        }}
                                    </div>
                                </div>
                            </div>
                            <div class="metric-card">
                                <div class="metric-icon click-icon">
                                    <i class="fa fa-chart-bar"></i>
                                </div>
                                <div class="metric-content">
                                    <div class="metric-label">Click Rate</div>
                                    <div class="metric-value">
                                        {{
                                            selectedCampaign
                                                ? selectedCampaign.clickRate
                                                : "0%"
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Message Preview -->
                    <div class="message-preview-section">
                        <h5 class="section-title">Message Preview</h5>
                        <div class="message-preview-card">
                            <div class="message-subject">
                                Subject:
                                {{
                                    selectedCampaign
                                        ? selectedCampaign.emailSubject ||
                                          selectedCampaign.name
                                        : ""
                                }}
                            </div>
                            <div class="message-body">
                                <p>Hi {{ name }},</p>
                                <p>
                                    {{
                                        selectedCampaign
                                            ? selectedCampaign.emailMessage ||
                                              `We're excited to share some exclusive offers with you as part of our ${selectedCampaign.name}.`
                                            : ""
                                    }}
                                </p>
                                <p>
                                    Click below to learn more and take advantage
                                    of this limited-time opportunity.
                                </p>
                            </div>
                            <div class="message-cta">
                                <b-button
                                    title="View Full Offer"
                                    variant="primary"
                                    class="cta-button"
                                >
                                    View Full Offer
                                </b-button>
                            </div>
                        </div>
                    </div>

                </div>

                <div class="modal-actions">
                    <b-button
                        variant="outline-primary"
                        @click="$refs.viewCampaignModal.hide()"
                    >
                        Close
                    </b-button>
                    <b-button
                        variant="primary"
                        @click="editCampaign"
                        title="Edit Campaign"
                    >
                        Edit Campaign
                    </b-button>
                    <b-button
                        v-if="
                            selectedCampaign &&
                            selectedCampaign.status === 'active'
                        "
                        variant="warning"
                        title="Pause Campaign"
                        @click="pauseCampaign"
                    >
                        Pause Campaign
                    </b-button>
                </div>
            </template>
        </b-modal>

        <!-- Add Campaign Modal -->
        <b-modal
            id="addCampaignModal"
            ref="addCampaignModal"
            size="xl"
            centered
            title="Create New Multi-Channel Campaign"
            modal-class="center-title-modal campaigns-modal"
            @hidden="resetModal"
        >
            <template slot="default">
                <p class="modal-subtitle">
                    Launch your campaign across multiple platforms with custom
                    images and messages.
                </p>

                <div class="new-campaign-body">
                    <b-row>
                        <!-- Left Column -->
                        <b-col cols="12" md="7">
                            <!-- Campaign Details -->
                            <div class="campaign-section">
                                <h5 class="section-heading">
                                    Campaign Details
                                </h5>

                                <b-form-group
                                    label-for="campaignName"
                                    class="campaign-form-group"
                                    :state="validationErrors.name ? false : null"
                                    :invalid-feedback="validationErrors.name"
                                >
                                    <template slot="label"
                                        >Campaign Name <span class="text-danger">*</span></template
                                    >
                                    <b-form-input
                                        id="campaignName"
                                        v-model="campaignForm.name"
                                        placeholder="e.g., Summer Sale 2026"
                                        @input="clearFieldError('name')"
                                    ></b-form-input>
                                </b-form-group>

                                <b-form-group
                                    label-for="targetAudience"
                                    class="campaign-form-group"
                                    :state="validationErrors.targetAudience ? false : null"
                                    :invalid-feedback="validationErrors.targetAudience"
                                >
                                    <template slot="label"
                                        >Target Audience <span class="text-danger">*</span></template
                                    >
                                    <b-form-input
                                        id="targetAudience"
                                        v-model="campaignForm.targetAudience"
                                        placeholder="e.g., All Active Leads"
                                        @input="clearFieldError('targetAudience')"
                                    ></b-form-input>
                                </b-form-group>

                                <b-row>
                                    <b-col cols="12" md="6">
                                        <b-form-group
                                            label-for="startDate"
                                            class="campaign-form-group"
                                            :state="validationErrors.startDate ? false : null"
                                            :invalid-feedback="validationErrors.startDate"
                                        >
                                            <template slot="label"
                                                >Start Date <span class="text-danger">*</span></template
                                            >
                                            <b-form-input
                                                id="startDate"
                                                type="date"
                                                v-model="campaignForm.startDate"
                                                @input="clearFieldError('startDate')"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6">
                                        <b-form-group
                                            label="End Date (Optional)"
                                            label-for="endDate"
                                            class="campaign-form-group"
                                        >
                                            <b-form-input
                                                id="endDate"
                                                type="date"
                                                v-model="campaignForm.endDate"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-form-group
                                    label="Hashtags"
                                    label-for="hashtags"
                                    class="campaign-form-group"
                                >
                                    <b-form-input
                                        id="hashtags"
                                        v-model="campaignForm.hashtags"
                                        placeholder="e.g., #SummerSale #LimitedD"
                                    ></b-form-input>
                                    <div class="form-tip">
                                        <i class="fa fa-info-circle"></i>
                                        <span
                                            >Add relevant hashtags separated by
                                            spaces for better social media
                                            reach.</span
                                        >
                                    </div>
                                </b-form-group>
                            </div>

                            <!-- Campaign Image -->
                            <div class="campaign-section">
                                <h5 class="section-heading">Campaign Image</h5>
                                <p class="section-subheading">
                                    Upload an image for your campaign posts
                                    across different channels.
                                </p>

                                <div
                                    v-if="!campaignForm.imagePreview"
                                    class="image-upload-area"
                                    :class="{ uploading: uploadingImage }"
                                    @click="
                                        !uploadingImage && triggerFileUpload()
                                    "
                                >
                                    <input
                                        ref="fileInput"
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg"
                                        @change="handleImageUpload"
                                        style="display: none"
                                        :disabled="uploadingImage"
                                    />
                                    <div class="upload-content">
                                        <i
                                            v-if="!uploadingImage"
                                            class="fa fa-image upload-icon"
                                        ></i>
                                        <i
                                            v-else
                                            class="fa fa-spinner fa-spin upload-icon"
                                        ></i>
                                        <p class="upload-text">
                                            {{
                                                uploadingImage
                                                    ? "Uploading image..."
                                                    : "Click to upload campaign image"
                                            }}
                                        </p>
                                        <p
                                            v-if="!uploadingImage"
                                            class="upload-hint"
                                        >
                                            PNG, JPG up to 5MB
                                        </p>
                                    </div>
                                </div>
                                <div v-else class="image-preview-container">
                                    <div class="image-preview-wrapper">
                                        <img
                                            :src="campaignForm.imagePreview"
                                            alt="Campaign image preview"
                                            class="image-preview"
                                        />
                                        <button
                                            type="button"
                                            class="remove-image-btn"
                                            @click="removeImage"
                                            aria-label="Remove image"
                                        >
                                            <i class="fa fa-times"></i>
                                        </button>
                                    </div>
                                    <div class="image-info">
                                        <p class="image-name">
                                            {{ campaignForm.imageName }}
                                        </p>
                                        <button
                                            type="button"
                                            class="change-image-btn"
                                            @click="triggerFileUpload"
                                        >
                                            Change Image
                                        </button>
                                    </div>
                                </div>

                                <div
                                    class="recommended-sizes"
                                    v-if="selectedChannels.length > 0"
                                >
                                    <div class="recommended-sizes-header">
                                        <i class="fa fa-file-alt"></i>
                                        <span>Recommended Image Sizes</span>
                                    </div>
                                    <div
                                        class="recommended-size-item"
                                        v-for="channel in selectedChannels"
                                        :key="channel.id"
                                    >
                                        <i :class="channel.icon"></i>
                                        <span>{{ channel.size }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Campaign Message -->
                            <div class="campaign-section">
                                <h5 class="section-heading">
                                    Campaign Message
                                </h5>

                                <b-form-group
                                    label-for="messageContent"
                                    class="campaign-form-group"
                                    :state="validationErrors.message ? false : null"
                                    :invalid-feedback="validationErrors.message"
                                >
                                    <template slot="label"
                                        >Message Content <span class="text-danger">*</span></template
                                    >
                                    <b-form-textarea
                                        id="messageContent"
                                        v-model="campaignForm.message"
                                        :placeholder="messagePlaceholder"
                                        rows="6"
                                        @input="clearFieldError('message')"
                                    ></b-form-textarea>
                                    <div class="form-tip">
                                        <i class="fa fa-info-circle"></i>
                                        <span v-html="messageTip"></span>
                                    </div>
                                </b-form-group>
                            </div>
                        </b-col>

                        <!-- Right Column -->
                        <b-col cols="12" md="5">
                            <div class="campaign-section">
                                <h5 class="section-heading">
                                    Distribution Channels
                                </h5>
                                <p class="section-subheading">
                                    Select platforms to publish this campaign
                                </p>

                                <div class="channels-list">
                                    <div
                                        v-for="channel in distributionChannels"
                                        :key="channel.id"
                                        :class="[
                                            'channel-card',
                                            {
                                                selected: isChannelSelected(
                                                    channel.id,
                                                ),
                                            },
                                        ]"
                                        @click="toggleChannel(channel.id)"
                                    >
                                        <div class="channel-info">
                                            <i :class="channel.icon"></i>
                                            <div class="channel-details">
                                                <div class="channel-name">
                                                    {{ channel.name }}
                                                </div>
                                                <div class="channel-size">
                                                    {{ channel.size }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="channel-checkbox">
                                            <input
                                                type="checkbox"
                                                :checked="
                                                    isChannelSelected(
                                                        channel.id,
                                                    )
                                                "
                                                tabindex="-1"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="selected-channels-section"
                                    v-if="selectedChannels.length > 0"
                                >
                                    <div class="selected-channels-header">
                                        <i class="fa fa-check-circle"></i>
                                        <span>Selected Channels</span>
                                    </div>
                                    <div class="selected-channels-tags">
                                        <b-badge
                                            v-for="channel in selectedChannels"
                                            :key="channel.id"
                                            class="channel-tag"
                                        >
                                            {{ channel.name }}
                                        </b-badge>
                                    </div>
                                </div>

                                <div
                                    v-if="validationErrors.channels"
                                    class="invalid-feedback d-block"
                                >
                                    {{ validationErrors.channels }}
                                </div>
                            </div>
                        </b-col>
                    </b-row>

                </div>
            </template>

            <template slot="modal-footer">
                <div class="modal-actions">
                    <b-button
                        variant="outline-primary"
                        @click="$refs.addCampaignModal.hide()"
                        title="Cancel"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        variant="primary"
                        @click="handleAddCampaign"
                        title="Create Campaign"
                    >
                        <i class="fa fa-paper-plane mr-2"></i>
                        Create Campaign
                    </b-button>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import ActionButtonGroup from "@/components/Common/ActionButtonGroup.vue";

const VALID_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const FORM_VALIDATIONS = [
    { field: "name", message: "Campaign name is required." },
    { field: "targetAudience", message: "Target audience is required." },
    { field: "startDate", message: "Start date is required." },
    { field: "message", message: "Message content is required." },
];

function getInitialCampaignForm() {
    return {
        name: "",
        type: "email",
        targetAudience: "All Active Leads",
        startDate: "",
        endDate: "",
        status: "scheduled",
        sent: 0,
        openRate: "0%",
        clickRate: "0%",
        hashtags: "",
        message: "",
        image: null,
        imageUrl: null,
        imagePreview: null,
        imageName: "",
    };
}

const DEFAULT_DISTRIBUTION_CHANNELS = [
    { id: "email", name: "Email", icon: "fa fa-envelope", size: "600x400px recommended" },
    { id: "sms", name: "SMS", icon: "fa fa-mobile", size: "Text only" },
    { id: "whatsapp", name: "WhatsApp", icon: "fab fa-whatsapp", size: "800x800px recommended" },
    { id: "facebook", name: "Facebook", icon: "fab fa-facebook-f", size: "1200x630px recommended" },
    { id: "linkedin", name: "LinkedIn", icon: "fab fa-linkedin-in", size: "1200x627px recommended" },
    { id: "instagram", name: "Instagram", icon: "fab fa-instagram", size: "1080x1080px square" },
    { id: "twitter", name: "Twitter / X", icon: "fab fa-twitter", size: "1200x675px recommended" },
];

export default {
    name: "CampaignsManagement",
    components: {
        ComponentHeader,
        ActionButtonGroup,
    },
    data() {
        return {
            searchQuery: "",
            currentPage: 1,
            perPage: 10,
            campaignForm: getInitialCampaignForm(),
            uploadingImage: false,
            distributionChannels: DEFAULT_DISTRIBUTION_CHANNELS,
            selectedChannels: [],
            validationErrors: {},
            name: "",
            messagePlaceholder:
                "Write your campaign message here. You can use variables like {{name}}, {{company}}, etc.",
            messageTip:
                "Tip: Use personalization variables like {&#123;name&#125;} for better engagement.",
            stats: {
                totalCampaigns: 5,
                activeCampaigns: 1,
                totalSent: 17500,
                avgOpenRate: "52.5%",
            },
            campaigns: [
                {
                    name: "Holiday Promotion 2025",
                    type: "email",
                    targetAudience: "All Active Leads",
                    startDate: "2025-12-15",
                    endDate: "2025-12-31",
                    status: "active",
                    sent: 6200,
                    openRate: "55%",
                    clickRate: "16.5%",
                },
                {
                    name: "Product Launch - Enterprise Edition",
                    type: "email",
                    targetAudience: "Qualified Leads",
                    startDate: "2025-11-20",
                    endDate: "2025-11-30",
                    status: "completed",
                    sent: 3500,
                    openRate: "65%",
                    clickRate: "18.5%",
                },
                {
                    name: "Webinar Invitation - CRM Best Practices",
                    type: "email",
                    targetAudience: "Sales Managers",
                    startDate: "2025-11-01",
                    endDate: "2025-11-15",
                    status: "completed",
                    sent: 2800,
                    openRate: "75%",
                    clickRate: "26.5%",
                },
                {
                    name: "New Year Special Offer",
                    type: "email",
                    targetAudience: "High-Value Leads",
                    startDate: "2026-01-05",
                    endDate: "2026-01-05",
                    status: "scheduled",
                    sent: 0,
                    openRate: "-",
                    clickRate: "-",
                },
                {
                    name: "Summer Sale 2025",
                    type: "notification",
                    targetAudience: "Website Leads",
                    startDate: "2025-06-01",
                    endDate: "2025-08-31",
                    status: "completed",
                    sent: 5000,
                    openRate: "45%",
                    clickRate: "13.5%",
                },
            ],
            tableFields: [
                { key: "name", label: "Campaign Name", sortable: true },
                { key: "type", label: "Type", sortable: true },
                {
                    key: "targetAudience",
                    label: "Target Audience",
                    sortable: true,
                },
                { key: "startEnd", label: "Start / End Date", sortable: true },
                { key: "status", label: "Status", sortable: true },
                { key: "sent", label: "Sent", sortable: true },
                { key: "openRate", label: "Open Rate", sortable: true },
                { key: "clickRate", label: "Click Rate", sortable: true },
                { key: "actions", label: "Actions", sortable: false },
            ],
            typeOptions: [
                { value: "email", text: "email" },
                { value: "notification", text: "notification" },
            ],
            statusSelectOptions: [
                { value: "scheduled", text: "scheduled" },
                { value: "active", text: "active" },
                { value: "completed", text: "completed" },
            ],
            selectedCampaign: null,
        };
    },
    computed: {
        filteredCampaigns() {
            let filtered = this.campaigns;

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(
                    (c) =>
                        c.name?.toLowerCase().includes(query) ||
                        c.targetAudience?.toLowerCase().includes(query) ||
                        c.type?.toLowerCase().includes(query),
                );
            }

            return filtered;
        },
        totalRows() {
            return this.filteredCampaigns.length;
        },
    },
    methods: {
        // Helper method to reset selected channels to default
        resetSelectedChannels() {
            const emailChannel = this.distributionChannels.find(
                (c) => c.id === "email",
            );
            this.selectedChannels = emailChannel ? [emailChannel] : [];
        },
        showAddCampaignModal() {
            this.$refs.addCampaignModal?.show();
        },
        resetModal() {
            this.campaignForm = getInitialCampaignForm();
            this.uploadingImage = false;
            this.validationErrors = {};
            this.resetSelectedChannels();
        },
        toggleChannel(channelId) {
            const channel = this.distributionChannels.find(
                (c) => c.id === channelId,
            );
            if (!channel) return;

            const index = this.selectedChannels.findIndex(
                (c) => c.id === channelId,
            );
            if (index > -1) {
                this.selectedChannels.splice(index, 1);
            } else {
                this.selectedChannels.push(channel);
            }
            this.clearFieldError("channels");
        },
        isChannelSelected(channelId) {
            return this.selectedChannels.some((c) => c.id === channelId);
        },
        triggerFileUpload() {
            this.$refs.fileInput?.click();
        },
        validateImageFile(file) {
            if (!VALID_IMAGE_TYPES.includes(file.type)) {
                this.$toasted?.error(
                    "Please upload a valid image file (PNG, JPG, JPEG)",
                );
                return false;
            }

            if (file.size > MAX_IMAGE_SIZE) {
                this.$toasted?.error("Image size must be less than 5MB");
                return false;
            }

            return true;
        },
        // Helper method to generate unique key for image upload
        generateImageKey() {
            return (
                Date.now().toString(36) +
                Math.random().toString(36).substr(2, 5)
            ).toLowerCase();
        },
        // Helper method to prepare image upload data
        prepareImageUploadData(imageDataUrl) {
            return {
                image: imageDataUrl,
                key: this.generateImageKey(),
                isPublic: true,
                replaceExisting: true,
                basePath: "campaign",
            };
        },
        // Helper method to handle successful image upload
        handleImageUploadSuccess(res, file, event) {
            const fileUrl = res.data?.fileUrl || res.data;
            this.campaignForm.imageUrl = fileUrl;
            this.campaignForm.image = file;
            this.$toasted?.success("Image uploaded successfully!");
        },
        handleImageUploadError(event) {
            this.campaignForm.imagePreview = null;
            this.campaignForm.imageName = "";
            this.campaignForm.imageUrl = null;
            this.campaignForm.image = null;
            const input = event?.target || this.$refs.fileInput;
            if (input) input.value = "";
        },
        async handleImageUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!this.validateImageFile(file)) {
                event.target.value = "";
                return;
            }

            this.uploadingImage = true;
            this.campaignForm.imageName = file.name;

            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    this.campaignForm.imagePreview = e.target.result;

                    const dataobj = this.prepareImageUploadData(e.target.result);
                    const res = await this.$store.dispatch(
                        "inventory/uploadImageToStorage",
                        dataobj,
                    );

                    if (res && res.data) {
                        this.handleImageUploadSuccess(res, file, event);
                    } else {
                        throw new Error("Upload failed");
                    }
                } catch (error) {
                    const msg =
                        error?.response?.data?.message ||
                        error?.message ||
                        "Image upload failed. Please try again.";
                    this.$toasted?.error(msg);
                    this.handleImageUploadError(event);
                } finally {
                    this.uploadingImage = false;
                }
            };

            reader.onerror = () => {
                this.$toasted?.error("Error reading image file");
                event.target.value = "";
                this.uploadingImage = false;
            };

            reader.readAsDataURL(file);
        },
        removeImage() {
            this.campaignForm.image = null;
            this.campaignForm.imageUrl = null;
            this.campaignForm.imagePreview = null;
            this.campaignForm.imageName = "";
            // Reset file input
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },
        validateCampaignForm() {
            const errors = {};

            for (const { field, message } of FORM_VALIDATIONS) {
                const value = this.campaignForm[field];
                if (!value || (typeof value === "string" && !value.trim())) {
                    errors[field] = message;
                }
            }

            if (this.selectedChannels.length === 0) {
                errors.channels =
                    "Please select at least one distribution channel.";
            }

            this.validationErrors = errors;
            return Object.keys(errors).length === 0;
        },
        clearFieldError(field) {
            if (this.validationErrors[field]) {
                this.$set(this.validationErrors, field, null);
            }
        },
        // Helper method to update campaign stats
        updateCampaignStats() {
            this.stats.totalCampaigns = this.campaigns.length;
            this.stats.totalSent = this.campaigns.reduce(
                (sum, c) => sum + (Number(c.sent) || 0),
                0,
            );
            this.stats.activeCampaigns = this.campaigns.filter(
                (c) => c.status === "active",
            ).length;
        },
        handleAddCampaign() {
            if (!this.validateCampaignForm()) {
                return;
            }

            const newCampaign = { ...this.campaignForm };
            // Set type based on first selected channel if not set
            if (this.selectedChannels.length > 0) {
                newCampaign.type = this.selectedChannels[0].id;
            }

            this.campaigns.unshift(newCampaign);
            this.updateCampaignStats();

            this.$refs.addCampaignModal.hide();
            this.$toasted?.success("Campaign created.");
        },
        getStatusVariant(status) {
            const mapping = {
                active: "success",
                completed: "secondary",
                scheduled: "info",
                paused: "warning",
            };
            return mapping[status] || "secondary";
        },
        formatNumber(val) {
            if (val == null) return "-";
            return String(val).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },
        formatDate(dateString) {
            return dateString || "-";
        },
        viewCampaigns(campaign) {
            this.selectedCampaign = {
                ...campaign,
                emailSubject: campaign.name,
                emailMessage: `We're excited to share some exclusive offers with you as part of our ${campaign.name}.`,
            };
            this.$refs.viewCampaignModal?.show();
        },
        editCampaign() {
            // Close view modal and open edit modal (or navigate to edit page)
            this.$refs.viewCampaignModal.hide();
            // For now, just show a message
            this.$toasted?.info("Edit campaign functionality coming soon.");
        },
        pauseCampaign() {
            if (!this.selectedCampaign) return;

            const campaignIndex = this.campaigns.findIndex(
                (c) => c.name === this.selectedCampaign.name,
            );
            if (campaignIndex !== -1) {
                this.campaigns[campaignIndex].status = "paused";
                this.selectedCampaign.status = "paused";
                this.updateCampaignStats();
                this.$toasted?.success("Campaign paused successfully.");
                this.$refs.viewCampaignModal.hide();
            }
        },
    },
};
</script>

<style scoped>
.card-wrap {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
}

.campaigns-table-card {
    border: 1px solid var(--slate-200, #e2e8f0);
}

.filter-card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.stats-card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    text-align: center;
}

/* Campaigns overview card */
.overview-card {
    border-radius: 0.75rem;
    align-items: center;
    border: 1px solid var(--slate-200, #e2e8f0);
    background: #fff;
    text-align: center;
}

/* Make overview cards more compact (b-card uses .card-body padding) */
::v-deep .overview-card .card-body {
    padding: 0.85rem 1rem;
}

.overview-label {
    font-size: 12px;
    color: var(--slate-600, #475569);
    margin-bottom: 0.25rem;
    font-weight: 600;
}
.overview-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--slate-900, #0f172a);
    line-height: 1.15;
}

.btn-new-campaign {
    border-radius: 1.25rem;
    padding: 0.5rem 1.25rem;
    font-weight: 600;
}

/* Badges for statuses */
.status-badge {
    padding: 0.45rem 0.75rem;
    border-radius: 0.5rem;
    font-weight: 600;
    text-transform: capitalize;
}
.status-badge.active {
    background-color: #e6f7ec;
    color: #198754;
}
.status-badge.completed {
    background-color: #f3e8ff;
    color: #8a2be2;
}
.status-badge.scheduled {
    background-color: #e8f1ff;
    color: #0d6efd;
}

.type-badge {
    background-color: #f1f6ff;
    color: #4966ff;
    padding: 0.35rem 0.6rem;
    border-radius: 0.5rem;
    text-transform: lowercase;
}

.stats-label {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
}

.stats-value {
    font-size: 1.75rem;
    font-weight: bold;
    color: #212529;
}

.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1.5rem 1.5rem 1rem 1.5rem;
    border-bottom: none;
}

.modal-header-content {
    flex: 1;
}

.modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #212529;
    text-align: justify;
}

.modal-subtitle {
    font-size: 12px;
    color: var(--slate-500, #64748b);
    margin: 0 0 0.75rem;
    padding: 0.75rem 1.5rem 0;
    text-align: center;
}

.campaign-modal-badges {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 0 1.5rem 0.75rem;
}

.modal-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 0 1.5rem 1.25rem;
}

.modal-actions .btn {
    min-width: 140px;
    border-radius: 10px;
    font-weight: 600;
}


.modal-header-custom .close {
    margin-left: 1rem;
    font-size: 1.5rem;
    font-weight: 300;
    line-height: 1;
    color: #000;
    opacity: 0.5;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.modal-header-custom .close:hover {
    opacity: 0.75;
}

.modal-tabs-wrapper {
    display: flex;
    gap: 0.5rem;
    padding: 0 1.5rem 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
}

.tab-button {
    border-radius: 0.375rem;
    padding: 0.5rem 1.5rem;
    font-weight: 500;
    border: 1px solid #dee2e6;
    transition: all 0.2s;
}

.tab-button.active {
    background-color: #fff;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    border-color: #dee2e6;
}

.tab-button:not(.active) {
    background-color: #f8f9fa;
    color: #6c757d;
}

.tab-button:not(.active):hover {
    background-color: #e9ecef;
    color: #212529;
}

.modal-content-wrapper {
    padding: 1.5rem;
}

.tab-content {
    min-height: 300px;
}

.modal-footer-custom {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #dee2e6;
}

.action-buttons {
    display: flex;
    align-items: center;
}

.action-buttons .btn-link {
    text-decoration: none;
}

.action-buttons .btn-link:hover {
    text-decoration: none;
}

.action-buttons .fa {
    font-size: 1.1rem;
}

.action-buttons .fa:hover {
    color: #212529 !important;
}

.pagination-wrapper {
    float: right;
}

::v-deep .custom-pagination .page-item.active .page-link {
    color: #fff;
}

::v-deep .custom-pagination .page-link {
    color: var(--slate-700, #334155);
}

::v-deep .modal-footer{
    justify-content: center;
}

::v-deep .campaigns-modal .modal-content {
    border-radius: 0.5rem;
}

::v-deep .campaigns-modal .modal-body {
    padding: 0;
}

::v-deep .table thead th {
    background-color: var(--slate-50, #f8fafc);
    border-bottom: 1px solid rgba(226, 232, 240, 0.8);
    font-weight: 800;
    color: var(--slate-700, #334155);
}

::v-deep .table td {
    vertical-align: middle;
}

/* Make table look like ~90% browser zoom (scoped) */
::v-deep .campaigns-table-card .table-responsive {
    transform: scale(0.9);
    transform-origin: top left;
    width: calc(100% / 0.9);
}

@media (max-width: 767.98px) {
    .pagination-wrapper {
        float: none;
        text-align: center;
        margin-top: 1rem;
    }
}

/* New Campaign Modal Styles */
.new-campaign-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1.5rem 1.5rem 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
}

.new-campaign-header-content {
    flex: 1;
}

.new-campaign-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #212529;
}

.new-campaign-subtitle {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0;
}

.new-campaign-close {
    margin-left: 1rem;
    font-size: 1.75rem;
    font-weight: 300;
    line-height: 1;
    color: #000;
    opacity: 0.5;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.new-campaign-close:hover {
    opacity: 0.75;
}

.new-campaign-body {
    padding: 1.5rem;
    max-height: 70vh;
    overflow-y: auto;
}

.campaign-section {
    margin-bottom: 2rem;
}

.section-heading {
    font-size: 1.125rem;
    font-weight: 700;
    color: #212529;
    margin-bottom: 0.5rem;
}

.section-subheading {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 1rem;
}

.campaign-form-group {
    margin-bottom: 1.25rem;
}

.campaign-form-group .text-danger {
    color: #dc3545 !important;
}

.campaign-form-group .invalid-feedback {
    display: block;
    color: #dc3545;
}

/* Fix duplicate calendar icons on date inputs */
::v-deep .campaign-form-group input[type="date"] {
    position: relative;
    padding-right: 0.75rem;
}

::v-deep
    .campaign-form-group
    input[type="date"]::-webkit-calendar-picker-indicator {
    position: absolute;
    right: 0.5rem;
    cursor: pointer;
    opacity: 1;
    width: 20px;
    height: 20px;
}

::v-deep .campaign-form-group input[type="date"]::-webkit-inner-spin-button,
::v-deep .campaign-form-group input[type="date"]::-webkit-clear-button {
    display: none;
    -webkit-appearance: none;
}

::v-deep .campaign-form-group input[type="date"]:after {
    display: none !important;
    content: none !important;
}

.form-tip {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #856404;
    background-color: #fff3cd;
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
}

.form-tip i {
    margin-top: 0.125rem;
    flex-shrink: 0;
}

.image-upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 0.5rem;
    padding: 3rem 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    background-color: #f8f9fa;
    margin-bottom: 1.5rem;
}

.image-upload-area:hover:not(.uploading) {
    border-color: #0d6efd;
    background-color: #f0f7ff;
}

.image-upload-area.uploading {
    cursor: not-allowed;
    opacity: 0.7;
}

.upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
}

.upload-icon {
    font-size: 3rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
}

.upload-text {
    font-size: 0.95rem;
    font-weight: 500;
    color: #212529;
    margin-bottom: 0.25rem;
}

.upload-hint {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0;
}

.image-preview-container {
    margin-bottom: 1.5rem;
}

.image-preview-wrapper {
    position: relative;
    width: 100%;
    max-width: 100%;
    border-radius: 0.5rem;
    overflow: hidden;
    border: 1px solid #dee2e6;
    background-color: #f8f9fa;
}

.image-preview {
    width: 100%;
    height: auto;
    max-height: 400px;
    object-fit: contain;
    display: block;
}

.remove-image-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.6);
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    z-index: 10;
}

.remove-image-btn:hover {
    background-color: rgba(220, 53, 69, 0.9);
    transform: scale(1.1);
}

.remove-image-btn i {
    font-size: 0.875rem;
}

.image-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;
    padding: 0.5rem 0;
}

.image-name {
    font-size: 0.875rem;
    color: #495057;
    margin: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 1rem;
}

.change-image-btn {
    background-color: transparent;
    border: 1px solid #0d6efd;
    color: #0d6efd;
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.change-image-btn:hover {
    background-color: #0d6efd;
    color: #fff;
}

.recommended-sizes {
    background-color: #f8f9fa;
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid #e9ecef;
}

.recommended-sizes-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #212529;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
}

.recommended-size-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
    font-size: 0.875rem;
    color: #495057;
}

.recommended-size-item i {
    color: #6c757d;
    width: 20px;
    text-align: center;
}

.channels-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
}

.channel-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    background-color: #fff;
}

.channel-card:hover {
    border-color: #0d6efd;
    background-color: #f0f7ff;
}

.channel-card.selected {
    border-color: #0d6efd;
    background-color: #f0f7ff;
    border-width: 2px;
}

.channel-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
}

.channel-info i {
    font-size: 1.5rem;
    color: #6c757d;
    width: 30px;
    text-align: center;
}

.channel-details {
    display: flex;
    flex-direction: column;
}

.channel-name {
    font-weight: 600;
    color: #212529;
    font-size: 0.95rem;
}

.channel-size {
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 0.25rem;
}

.channel-checkbox {
    margin-left: 1rem;
}

.channel-checkbox input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    pointer-events: none;
}

.selected-channels-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e9ecef;
}

.selected-channels-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #212529;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
}

.selected-channels-header i {
    color: #198754;
}

.selected-channels-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.channel-tag {
    background-color: #0d6efd;
    color: #fff;
    padding: 0.375rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    font-weight: 500;
}

.new-campaign-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #dee2e6;
}

.cancel-btn {
    border-radius: 0.5rem;
    padding: 0.5rem 1.25rem;
    font-weight: 500;
    border: 1px solid #dee2e6;
}

.create-btn {
    border-radius: 0.5rem;
    padding: 0.5rem 1.5rem;
    font-weight: 600;
}

@media (max-width: 768px) {
    .new-campaign-body {
        max-height: 60vh;
    }

    .image-upload-area {
        padding: 2rem 1rem;
    }
}

/* View Campaign Modal Styles */
.view-campaign-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1.5rem 1.5rem 1rem 1.5rem;
    border-bottom: none;
}

.view-campaign-header-content {
    flex: 1;
}

.view-campaign-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
    color: #212529;
}

.view-campaign-badges {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.campaign-status-badge {
    padding: 0.35rem 0.75rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: capitalize;
}

.campaign-status-badge.active {
    background-color: #e6f7ec;
    color: #198754;
}

.campaign-status-badge.completed {
    background-color: #f3e8ff;
    color: #8a2be2;
}

.campaign-status-badge.scheduled {
    background-color: #e8f1ff;
    color: #0d6efd;
}

.campaign-type-badge {
    background-color: #f1f6ff;
    color: #4966ff;
    padding: 0.35rem 0.75rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: lowercase;
}

.view-campaign-close {
    margin-left: 1rem;
    font-size: 1.75rem;
    font-weight: 300;
    line-height: 1;
    color: #000;
    opacity: 0.5;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.view-campaign-close:hover {
    opacity: 0.75;
}

.view-campaign-body {
    padding: 1.5rem;
    max-height: 60vh;
    overflow-y: auto;
}

.campaign-details-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e9ecef;
}

.campaign-detail-item {
    display: flex;
    flex-direction: column;
}

.campaign-detail-label {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.campaign-detail-value {
    font-size: 1rem;
    color: #212529;
    font-weight: 600;
}

.performance-metrics-section {
    margin-bottom: 2rem;
}

.section-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #212529;
    margin-bottom: 1rem;
}

.metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
}

.metric-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.75rem;
    border: 1px solid #e9ecef;
}

.metric-icon {
    width: 40px;
    height: 40px;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    flex-shrink: 0;
}

.metric-icon.sent-icon {
    background-color: #e3f2fd;
    color: #1976d2;
}

.metric-icon.open-icon {
    background-color: #e8f5e9;
    color: #388e3c;
}

.metric-icon.click-icon {
    background-color: #f3e5f5;
    color: #7b1fa2;
}

.metric-content {
    flex: 1;
}

.metric-label {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
}

.metric-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #212529;
}

.message-preview-section {
    margin-bottom: 1rem;
}

.message-preview-card {
    background-color: #f8f9fa;
    border-radius: 0.75rem;
    padding: 1.5rem;
    border: 1px solid #e9ecef;
}

.message-subject {
    font-size: 0.95rem;
    font-weight: 600;
    color: #212529;
    margin-bottom: 1rem;
}

.message-body {
    font-size: 0.9rem;
    color: #495057;
    line-height: 1.6;
    margin-bottom: 1rem;
}

.message-body p {
    margin-bottom: 0.75rem;
}

.message-body p:last-child {
    margin-bottom: 0;
}

.message-cta {
    margin-top: 1rem;
}

.cta-button {
    border-radius: 0.5rem;
    padding: 0.5rem 1.5rem;
    font-weight: 600;
}

.view-campaign-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #dee2e6;
}

.footer-action-btn {
    border-radius: 0.5rem;
    padding: 0.5rem 1.25rem;
    font-weight: 500;
}
.new-campaign-footer {
    display: flex;
    justify-content: center;   
    align-items: center;
    width: 100%;
    padding: 0;              
    margin-top: 0;  
     border-top: none !important;
}              

.new-campaign-footer > .btn {
    margin: 0 8px;            
}

@media (max-width: 768px) {
    .campaign-details-section {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .metrics-grid {
        grid-template-columns: 1fr;
    }

    .view-campaign-body {
        max-height: 50vh;
    }
  

}
</style>
<style lang="sass" scoped>
.action-btn-group
  display: flex
  align-items: center

.button-circle
    margin-right: 0.5rem
    &:last-child
    margin-right: 0


@media (max-width: 576px)
  .action-btn-group
    flex-direction: row
    align-items: center

    .button-circle
      margin-right: 0
      margin-bottom: 0.5rem

      &:last-child
        margin-bottom: 0
</style>
